import EventEmitter from 'events'

export enum SOCKET_MESSAGES {
  ROBOT_TELEMETRY = 'ros.telemetry',
  GAUSIUM_LIVE_EXECUTION = 'gausium_live_execution',
  MAPPING_LOCAL_SNAPSHOT = 'mapping_local.snapshot',
  MAPPING_LOCAL_ODOMETRY = 'mapping_local.odometry',
  FMS_NOTIFICATION = 'fms.notification',
  ROBOT_LASERSCAN = 'ros.laserscan',
}

const TIME_TO_RECONNECT = 2000

const WEBSOCKET_PATH = '/api/websocket/'

const DEBUG_STORAGE_KEY = 'fmsWsDebug'

type WsSnapshot = {
  connectionId: number
  readyState: number
  opened: boolean
  lastOpenAt: number | null
  lastClose: { at: number; code: number; reason: string; wasClean: boolean } | null
  lastInboundAt: number | null
  lastInboundType: string | null
  inboundCountThisConnection: number
  lastOutboundAt: number | null
  lastOutboundType: string | null
  outboundThisConnection: string[]
  desiredSubscriptions: string[]
  prevConnectionHadSubscribe: boolean
  diagnose: string
}

function wsDebugEnabled(): boolean {
  try {
    if (typeof window === 'undefined') return false
    if (window.localStorage?.getItem(DEBUG_STORAGE_KEY) === '1') return true
    return new URLSearchParams(window.location.search).get('wsdebug') === '1'
  } catch {
    return false
  }
}

function parseMessageType(raw: string): string | null {
  try {
    const parsed = JSON.parse(raw)
    return typeof parsed?.type === 'string' ? parsed.type : null
  } catch {
    return null
  }
}

function isSubscribeType(type: string | null): boolean {
  return Boolean(type && type.endsWith('.subscribe'))
}

function parseJsonObject(raw: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/** Stable key so reconnect can replay the same subscribe payload. */
function subscriptionKey(parsed: Record<string, unknown>): string | null {
  const type = typeof parsed.type === 'string' ? parsed.type : null
  if (!type) return null
  const subscribeType = type.endsWith('.unsubscribe')
    ? `${type.slice(0, -'.unsubscribe'.length)}.subscribe`
    : type.endsWith('.subscribe')
      ? type
      : null
  if (!subscribeType) return null
  if (subscribeType === 'gausium_live_execution.subscribe') {
    return subscribeType
  }
  const serial = parsed.robot_serial_number
  if (typeof serial === 'string' && serial) {
    return `${subscribeType}:${serial}`
  }
  const robotId = parsed.robot_id
  if (typeof robotId === 'number' || typeof robotId === 'string') {
    return `${subscribeType}:${robotId}`
  }
  return subscribeType
}

function diagnoseSnapshot(s: Omit<WsSnapshot, 'diagnose'>): string {
  const now = Date.now()
  const openForMs = s.lastOpenAt != null && s.opened ? now - s.lastOpenAt : null
  const inboundAgeMs = s.lastInboundAt != null ? now - s.lastInboundAt : null
  const hasSubscribe =
    s.outboundThisConnection.some(t => isSubscribeType(t)) || s.desiredSubscriptions.some(t => isSubscribeType(t))

  if (!s.opened) {
    return 'socket not open'
  }
  if (s.prevConnectionHadSubscribe && !hasSubscribe && openForMs != null && openForMs > 4000) {
    return 'resubscribe-gap: previous connection had subscribe, this one has none — REST polling can still work'
  }
  if (hasSubscribe && inboundAgeMs != null && inboundAgeMs > 20000) {
    return 'subscribed but no inbound for >20s — server/group idle or half-open'
  }
  if (hasSubscribe && s.inboundCountThisConnection > 0) {
    return 'ok: subscribed and receiving'
  }
  if (hasSubscribe) {
    return 'subscribed, waiting for first inbound'
  }
  if (inboundAgeMs != null && inboundAgeMs < 20000) {
    return 'ok: inbound without client subscribe (e.g. notifications group on connect)'
  }
  return 'open, no subscribe on this connection (idle notifications-only or resubscribe-gap)'
}

declare global {
  interface Window {
    __FMS_WS__?: {
      snapshot: () => WsSnapshot
      forceClose: () => void
      enableDebug: () => void
      disableDebug: () => void
    }
  }
}

class WebsocketManager {
  static instance: WebsocketManager | undefined
  socket: WebSocket
  eventEmitter: EventEmitter
  opened: boolean

  private connectionId = 0
  private lastOpenAt: number | null = null
  private lastClose: WsSnapshot['lastClose'] = null
  private lastInboundAt: number | null = null
  private lastInboundType: string | null = null
  private inboundCountThisConnection = 0
  private lastOutboundAt: number | null = null
  private lastOutboundType: string | null = null
  private outboundThisConnection: string[] = []
  private desiredSubscriptions = new Map<string, string>()
  private prevConnectionHadSubscribe = false
  private watchdogTimer: number | null = null
  private gapWarnedForConnection = 0

  private constructor() {
    this.opened = false
    this.eventEmitter = new EventEmitter()
    this.socket = this.getSocket()
    this.installDebugApi()
  }

  private log(...args: unknown[]) {
    if (wsDebugEnabled()) {
      // eslint-disable-next-line no-console
      console.info('[FMS WS]', ...args)
    }
  }

  private snapshot = (): WsSnapshot => {
    const base = {
      connectionId: this.connectionId,
      readyState: this.socket?.readyState ?? WebSocket.CLOSED,
      opened: this.opened,
      lastOpenAt: this.lastOpenAt,
      lastClose: this.lastClose,
      lastInboundAt: this.lastInboundAt,
      lastInboundType: this.lastInboundType,
      inboundCountThisConnection: this.inboundCountThisConnection,
      lastOutboundAt: this.lastOutboundAt,
      lastOutboundType: this.lastOutboundType,
      outboundThisConnection: [...this.outboundThisConnection],
      desiredSubscriptions: Array.from(this.desiredSubscriptions.values())
        .map(parseMessageType)
        .filter((t): t is string => Boolean(t)),
      prevConnectionHadSubscribe: this.prevConnectionHadSubscribe,
    }
    return { ...base, diagnose: diagnoseSnapshot(base) }
  }

  private installDebugApi() {
    if (typeof window === 'undefined') return
    window.__FMS_WS__ = {
      snapshot: this.snapshot,
      forceClose: () => {
        this.log('forceClose() — simulates proxy/NAT drop; watch diagnose after reconnect')
        try {
          this.socket.close(4000, 'debug-force-close')
        } catch {
          /* ignore */
        }
      },
      enableDebug: () => {
        window.localStorage.setItem(DEBUG_STORAGE_KEY, '1')
        this.log('debug on; snapshot / forceClose available')
      },
      disableDebug: () => {
        window.localStorage.removeItem(DEBUG_STORAGE_KEY)
      },
    }
  }

  private startWatchdog() {
    if (this.watchdogTimer != null) {
      window.clearInterval(this.watchdogTimer)
    }
    this.watchdogTimer = window.setInterval(() => {
      if (!wsDebugEnabled() || !this.opened) return
      const snap = this.snapshot()
      if (snap.diagnose.startsWith('resubscribe-gap') && this.gapWarnedForConnection !== this.connectionId) {
        this.gapWarnedForConnection = this.connectionId
        // eslint-disable-next-line no-console
        console.warn('[FMS WS]', snap.diagnose, snap)
      }
    }, 4000)
  }

  private getSocket() {
    this.opened = false
    const scheme = 'ws' + (window.location.protocol === 'https:' ? 's' : '')
    const url = `${scheme}://${window.location.host}${WEBSOCKET_PATH}`

    const socket = new WebSocket(url)
    socket.onmessage = this.handleMessage
    socket.onopen = this.handleOpen
    socket.onerror = this.handleError
    socket.onclose = this.handleClose
    return socket
  }

  private handleMessage = (event: MessageEvent) => {
    try {
      const messageParsed = JSON.parse(event.data)
      this.lastInboundAt = Date.now()
      this.lastInboundType = typeof messageParsed?.type === 'string' ? messageParsed.type : null
      this.inboundCountThisConnection += 1
      if (this.inboundCountThisConnection === 1) {
        this.log('first inbound on connection', this.connectionId, this.lastInboundType)
      }
      this.eventEmitter.emit(messageParsed.type, messageParsed.data)
    } catch (error) {
      console.error('[WebSocket] Failed to parse WebSocket message:', error)
    }
  }

  private handleOpen = () => {
    this.opened = true
    this.connectionId += 1
    this.lastOpenAt = Date.now()
    this.inboundCountThisConnection = 0
    this.lastInboundAt = null
    this.lastInboundType = null
    this.lastOutboundAt = null
    this.lastOutboundType = null
    this.outboundThisConnection = []
    this.log('open', {
      connectionId: this.connectionId,
      prevHadSubscribe: this.prevConnectionHadSubscribe,
      desired: this.desiredSubscriptions.size,
    })
    this.replayDesiredSubscriptions()
    this.startWatchdog()
  }

  private replayDesiredSubscriptions() {
    if (!this.opened || this.desiredSubscriptions.size === 0) return
    this.log('replay subscriptions', this.desiredSubscriptions.size)
    Array.from(this.desiredSubscriptions.values()).forEach(payload => {
      try {
        this.noteOutbound(payload)
        this.socket.send(payload)
      } catch (error) {
        console.error('[WebSocket] Failed to replay subscribe:', error)
      }
    })
  }

  private handleError = (error: Event) => {
    console.error('[WebSocket] Connection error:', error)
  }

  private handleClose = (event: CloseEvent) => {
    this.opened = false
    this.prevConnectionHadSubscribe =
      this.desiredSubscriptions.size > 0 || this.outboundThisConnection.some(t => isSubscribeType(t))
    this.lastClose = {
      at: Date.now(),
      code: event.code,
      reason: event.reason || '',
      wasClean: event.wasClean,
    }
    this.log('close', this.lastClose, { prevHadSubscribe: this.prevConnectionHadSubscribe })
    setTimeout(() => {
      this.socket = this.getSocket()
    }, TIME_TO_RECONNECT)
  }

  private noteOutbound(data: string) {
    const type = parseMessageType(data)
    this.lastOutboundAt = Date.now()
    this.lastOutboundType = type
    if (type && !this.outboundThisConnection.includes(type)) {
      this.outboundThisConnection.push(type)
    }
    if (type && (type.endsWith('.subscribe') || type.endsWith('.unsubscribe'))) {
      this.log('send', type, { connectionId: this.connectionId })
    }
  }

  private rememberDesired(data: string) {
    const parsed = parseJsonObject(data)
    if (!parsed) return
    const type = typeof parsed.type === 'string' ? parsed.type : null
    const key = subscriptionKey(parsed)
    if (!key) return
    if (type && type.endsWith('.subscribe')) {
      this.desiredSubscriptions.set(key, data)
    } else if (type && type.endsWith('.unsubscribe')) {
      this.desiredSubscriptions.delete(key)
    }
  }

  public static addEventListener(message: SOCKET_MESSAGES, listener: (...args: any[]) => void): void {
    if (!WebsocketManager.instance) {
      WebsocketManager.instance = new WebsocketManager()
    }
    WebsocketManager.instance.eventEmitter.addListener(message, listener)
  }

  public static removeEventListener(message: SOCKET_MESSAGES, listener: (...args: any[]) => void): void {
    if (!WebsocketManager.instance) {
      WebsocketManager.instance = new WebsocketManager()
    }
    WebsocketManager.instance.eventEmitter.removeListener(message, listener)
  }

  public static listenerCount(message: SOCKET_MESSAGES): number {
    return WebsocketManager.instance?.eventEmitter.listenerCount(message) ?? 0
  }

  public static sendMessage(data: string): void {
    if (!WebsocketManager.instance) {
      WebsocketManager.instance = new WebsocketManager()
    }
    const manager = WebsocketManager.instance
    manager.rememberDesired(data)
    if (manager.opened && manager.socket.readyState === WebSocket.OPEN) {
      manager.noteOutbound(data)
      manager.socket.send(data)
    }
  }

  public static disconnect(): void {
    if (!WebsocketManager.instance || WebsocketManager.instance.socket.readyState !== 1) {
      return
    }
    WebsocketManager.instance.socket.close()
    WebsocketManager.instance = undefined
  }
}

export default WebsocketManager
